import { __assign, __extends } from "tslib";
import { AppInsightsCore } from '@microsoft/1ds-core-js';
import { PostChannel } from '@microsoft/1ds-post-js';
import { logNotification } from '@microsoft/oteljs';
import { PerfManager } from './PerfManager';
export function getAppInsights(oneDSSinkProperties, instrumentationKey) {
    var httpXHROverride = oneDSSinkProperties.httpXHROverride;
    var appInsightsConfig = {
        instrumentationKey: instrumentationKey,
        endpointUrl: oneDSSinkProperties.endpointUrl,
        channelConfiguration: {
            eventsLimitInMem: oneDSSinkProperties.eventsLimitInMem,
            httpXHROverride: httpXHROverride,
            setTimeoutOverride: oneDSSinkProperties.setTimeoutOverride,
            clearTimeoutOverride: oneDSSinkProperties.clearTimeoutOverride,
            ignoreMc1Ms0CookieProcessing: true,
            // 1ds-post-js in 3.1.x adds some optimization code that purportedly improves overall performance, but
            // in our testing regresses synchronous API performance. Disabling it here.
            disableOptimizeObj: true
        },
        // Force a "w" parameter on the URL string, which tells the Collector that this is a web request and that it
        // should parse the user agent string to determine the browser name and version
        // In the URL string, this looks like "&w=2"
        //   (the presence of a "w" string indicates it's a web request, and "2" means disable cookies)
        // The 1DS SDK generally automatically sets the "w" parameter when it detects the active document (i.e. webpage),
        // but in the web worker it doesn't have access to the document, so we have to force it.
        // As a good heuristic, we are probably in a web environment (i.e. not Node.js) if we didn't have to override XHR.
        // This should be sufficient for our purposes, and covers the web worker scenario.
        enableWParam: !httpXHROverride,
        // Sets the value of the "w" parameter to 2, which means "disable cookies"
        disableCookiesUsage: true,
        extensionConfig: __assign({}, oneDSSinkProperties.extensionConfig)
    };
    if (oneDSSinkProperties.stats && oneDSSinkProperties.stats.networkStats && appInsightsConfig.channelConfiguration) {
        appInsightsConfig.channelConfiguration.payloadListener = function (originalPayload, modifiedPayload) {
            var _a;
            var actualPayload = modifiedPayload ? modifiedPayload : originalPayload;
            if (actualPayload.data) {
                (_a = oneDSSinkProperties.stats) === null || _a === void 0 ? void 0 : _a.networkStats(actualPayload.data.length);
            }
        };
    }
    var applicationInsights = new ApplicationInsights();
    applicationInsights.initialize(appInsightsConfig, oneDSSinkProperties.plugins);
    applicationInsights.setUploadFrequency(oneDSSinkProperties.uploadFrequency);
    if (oneDSSinkProperties.notificationListener) {
        applicationInsights.addNotificationListener(oneDSSinkProperties.notificationListener);
    }
    if (oneDSSinkProperties.stats) {
        applicationInsights.setPerfMgr(new PerfManager(oneDSSinkProperties.stats));
    }
    return applicationInsights;
}
export var hookUp1DSNotifications = function (oneDSSink, applicationInsights) {
    if (applicationInsights) {
        applicationInsights.addNotificationListener({
            eventsSent: function (events) {
                logNotification(2 /* LogLevel.Info */, 2 /* Category.Transport */, function () {
                    return "Successfully sent ".concat(events.length, " event(s)");
                });
                logNotification(3 /* LogLevel.Verbose */, 2 /* Category.Transport */, function () {
                    return "Sent event(s) details : ".concat(JSON.stringify(events, null, 2));
                });
                oneDSSink.eventsSent += events.length;
            },
            eventsDiscarded: function (events, reason) {
                logNotification(0 /* LogLevel.Error */, 2 /* Category.Transport */, function () {
                    return "Discarded ".concat(events.length, " event(s) because ").concat(reason);
                });
                logNotification(3 /* LogLevel.Verbose */, 2 /* Category.Transport */, function () {
                    return "Discarded event(s) details : ".concat(JSON.stringify(events, null, 2));
                });
                oneDSSink.eventsDiscarded += events.length;
            }
        });
    }
};
var ApplicationInsights = /** @class */ (function (_super) {
    __extends(ApplicationInsights, _super);
    function ApplicationInsights() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Initialize the sdk.
     * @param {IExtendedConfiguration} config - The configuration to initialize the SDK.
     * @param {IPlugin[]} extensions          - An array of extensions that are to be used by the core.
     */
    ApplicationInsights.prototype.initialize = function (config, extensions) {
        this._postChannel = new PostChannel();
        var plugins = extensions !== null && extensions !== void 0 ? extensions : [];
        config.channels = [[this._postChannel]];
        config.extensionConfig = config.extensionConfig || [];
        config.extensionConfig[this._postChannel.identifier] = __assign(__assign({}, config.channelConfiguration), config.extensionConfig[this._postChannel.identifier]);
        try {
            _super.prototype.initialize.call(this, config, plugins);
        }
        catch (error) {
            this.logger.warnToConsole('Failed to initialize SDK.' + error);
        }
    };
    ApplicationInsights.prototype.setUploadFrequency = function (uploadFrequency) {
        if (!this._postChannel || !uploadFrequency) {
            return;
        }
        // OneDS SDK allows to specify "Latency" of the event (think priority). At the moment the SDK supports two meaningful latencies
        // (three if you look at the enum definition, but the third one is not used) - EventLatency.Normal (default) and EventLatency.RealTime.
        // The idea here is to upload events with RealTime latency at a higher frequency than the events with Normal latency. To determine the
        // upload frequency for the event, OneDS uses the "concept" of transmission profile. A transmission profile is simply a name that is
        // associated with an array of two numbers - { 'Name' = [Normal, RealTime] }. OneDS documentation specifies that each latency timer
        // needs to be a multiple of the latency higher than it - more on this later.
        // There are three built in profiles:
        //  - RT_PROFILE     = [2, 1]; (default)
        //  - NRT_PROFILE    = [6, 3];
        //  - BE_PROFILE     = [18, 9];
        // Now here is the fun part, only the RealTime latency frequency is used in the code - the code simply doubles the RealTime latency
        //  frequency and uses it for Normal latency frequency - i.e. Normal = RealTime * 2 (see PostChannel._batchAndSendEvents for details).
        // OTel SDK does not expose RealTime latency and uses the default Normal one. So the upload frequency that we are interested here is
        // the Normal one, and to keep things sane for the consumers of the SDK that's what the uploadFrequency parameter will be mapped to.
        var normalLatencyFrequency = uploadFrequency / 1000;
        var realtimeLatencyFrequency = normalLatencyFrequency / 2;
        var customProfiles = {};
        customProfiles['OTelCustomTransmissionProfile'] = [normalLatencyFrequency, realtimeLatencyFrequency];
        this._postChannel._loadTransmitProfiles(customProfiles);
        this._postChannel._setTransmitProfile('OTelCustomTransmissionProfile');
    };
    ApplicationInsights.prototype.flush = function (async) {
        if (this._postChannel) {
            this._postChannel.flush(async);
        }
    };
    ApplicationInsights.prototype.shutdown = function () {
        if (this._postChannel) {
            this._postChannel.teardown();
        }
    };
    return ApplicationInsights;
}(AppInsightsCore));
export { ApplicationInsights };
//# sourceMappingURL=ApplicationInsights.js.map